import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  useComponentOntologyFields,
  useTicketOntologyFields,
} from 'features/libra/ontology/useOntologyFields'
import { useStepNavigation } from 'features/libra/StepNavigationContext'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { useGeckoPermissions } from 'features/permissions'
import isEqual from 'lodash/isEqual'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { RichTextContent } from 'types'

import { type AnchorLabelContext, getAnchorLabel } from './anchorLabels'
import { matchAnchors } from './anchorMatching'
import { type AnchorValueContext, resolveCurrentValue } from './anchorValues'
import { CommentBubble, NewCommentBubble } from './CommentBubble'
import { useCommentLayout } from './CommentLayoutProvider'
import { useComments } from './CommentsProvider'
import {
  type CommentAnchor,
  type CommentThread,
  getAnchorKey,
} from './commentTypes'

function isThreadOutdated(
  thread: CommentThread,
  ctx: AnchorValueContext
): boolean {
  if (thread.valueAtCreation === undefined) return false
  const current = resolveCurrentValue(thread.anchor, ctx)
  if (current === undefined) return false
  return !isEqual(thread.valueAtCreation, current)
}

export const CommentSidebar: FC<{ visible: boolean }> = ({ visible }) => {
  const {
    threads,
    focusedAnchor,
    setFocusedAnchor,
    composingAnchor,
    expandedThreadId,
    expandThread,
    collapseThread,
    createThread,
    replyToThread,
    resolveThread,
    reopenThread,
    deleteThread,
    deleteComment,
    clearSelection,
    viewMode,
    filterMode,
  } = useComments()

  const { navigateToStep, activeStep } = useStepNavigation()
  const activeStepId = activeStep?.id ?? ''
  const workflow = useWorkflow()
  const { ontologyFields } = useTicketOntologyFields()
  // Also pull the component-scope ontology so anchor labels for
  // component-scoped anchors resolve their display names (rather than
  // falling back to the raw fieldId). `componentNames` remains unset —
  // labels will use the raw component id until a follow-up threads the
  // asset-model component list into the sidebar.
  const { ontologyFields: componentOntologyFields } =
    useComponentOntologyFields()
  const { user } = useGeckoPermissions()
  const { fieldValues } = useTicketAccess()
  const { anchorPositions, scrollToAnchor } = useCommentLayout()
  const listRef = useRef<HTMLDivElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT_WIDTH)
  const [isResizing, setIsResizing] = useState(false)

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }, [])

  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      const container = containerRef.current
      if (!container) return
      const containerRect = container.getBoundingClientRect()
      const newWidth = containerRect.right - e.clientX
      setSidebarWidth(
        Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, newWidth))
      )
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing])

  const zones = useMemo(
    () =>
      unwrapFieldValue<Array<Record<string, unknown>>>(fieldValues.zones) ?? [],
    [fieldValues.zones]
  )

  const labelCtx = useMemo<AnchorLabelContext>(
    () => ({
      workflow,
      ontologyFields,
      componentOntologyFields,
      stepSpecificData: { zones },
    }),
    [workflow, ontologyFields, componentOntologyFields, zones]
  )

  const valueCtx = useMemo<AnchorValueContext>(
    () => ({
      fieldValues,
      zones: zones as AnchorValueContext['zones'],
    }),
    [fieldValues, zones]
  )

  const currentUserId = user?.id ?? user?.email ?? ''

  const { sortedGroups, orphaned } = useMemo(() => {
    const validStepIds = new Set(workflow.steps.map((s) => s.id))
    const groups = new Map<string, CommentThread[]>()
    const orphanedList: CommentThread[] = []

    for (const thread of threads) {
      if (validStepIds.has(thread.anchor.stepId)) {
        const list = groups.get(thread.anchor.stepId)
        if (list) list.push(thread)
        else groups.set(thread.anchor.stepId, [thread])
      } else {
        orphanedList.push(thread)
      }
    }

    for (const list of groups.values()) {
      // Primary: page position (top-to-bottom, Google Docs style).
      // Tiebreaker: newest thread first when multiple threads share the same anchor.
      list.sort((a, b) => {
        const yA = anchorPositions.get(getAnchorKey(a.anchor)) ?? Infinity
        const yB = anchorPositions.get(getAnchorKey(b.anchor)) ?? Infinity
        if (yA !== yB) return yA - yB
        const createdA = a.comments[0]?.createdAt ?? ''
        const createdB = b.comments[0]?.createdAt ?? ''
        return createdB.localeCompare(createdA)
      })
    }

    return { sortedGroups: groups, orphaned: orphanedList }
  }, [threads, workflow.steps, anchorPositions])

  const isNewCommentMode = composingAnchor !== null

  const handleCreateThread = useCallback(
    async (anchor: CommentAnchor, text: RichTextContent) => {
      const valueSnapshot = resolveCurrentValue(anchor, valueCtx)
      clearSelection()
      await createThread(anchor, text, valueSnapshot)
    },
    [createThread, valueCtx, clearSelection]
  )

  useEffect(() => {
    if (!expandedThreadId && !focusedAnchor) return
    const container = listRef.current
    if (!container) return

    const targetId = expandedThreadId ?? 'new-comment'
    const el = container.querySelector(`[data-thread-id="${targetId}"]`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [expandedThreadId, focusedAnchor])

  const currentStepThreads = sortedGroups.get(activeStepId) ?? []

  const filterThread = (t: CommentThread) => {
    if (filterMode === 'active') return !t.resolved
    if (filterMode === 'resolved') return t.resolved
    return true
  }

  const filteredOrphaned = orphaned.filter(filterThread)

  const newCommentBubbleNode =
    isNewCommentMode && composingAnchor ? (
      <div key='new-comment' data-thread-id='new-comment'>
        <NewCommentBubble
          anchorLabel={getAnchorLabel(composingAnchor, labelCtx)}
          onSubmit={(text) => handleCreateThread(composingAnchor, text)}
          onCancel={() => clearSelection()}
        />
      </div>
    ) : null

  // Renders a sorted thread list with the new-comment bubble spliced in at the
  // correct page position, so it appears in-line rather than always on top.
  const renderThreadsWithNewComment = (
    list: CommentThread[],
    showBubble: boolean,
    renderThread: (t: CommentThread) => React.ReactNode
  ): React.ReactNode[] => {
    const nodes = list.map(renderThread)
    if (!showBubble || !composingAnchor || !newCommentBubbleNode) return nodes
    const newY = anchorPositions.get(getAnchorKey(composingAnchor)) ?? Infinity
    const insertIdx = list.findIndex(
      (t) => (anchorPositions.get(getAnchorKey(t.anchor)) ?? Infinity) > newY
    )
    nodes.splice(
      insertIdx === -1 ? nodes.length : insertIdx,
      0,
      newCommentBubbleNode
    )
    return nodes
  }

  const renderBubble = (thread: CommentThread, onExpandExtra?: () => void) => (
    <div key={thread.entryId} data-thread-id={thread.entryId}>
      <CommentBubble
        thread={thread}
        labelCtx={labelCtx}
        isExpanded={expandedThreadId === thread.entryId}
        isFocused={
          focusedAnchor !== null && matchAnchors(thread.anchor, focusedAnchor)
        }
        isOutdated={isThreadOutdated(thread, valueCtx)}
        onExpand={() => {
          expandThread(thread.entryId)
          setFocusedAnchor(thread.anchor)
          onExpandExtra?.()
        }}
        onCollapse={() => {
          collapseThread()
          setFocusedAnchor(null)
        }}
        onReply={(text) => replyToThread(thread.entryId, text)}
        onResolve={() => resolveThread(thread.entryId)}
        onReopen={() => reopenThread(thread.entryId)}
        onDelete={() => {
          deleteThread(thread.entryId)
          clearSelection()
        }}
        onDeleteComment={(commentId) =>
          deleteComment(commentId, thread.entryId)
        }
        currentUserId={currentUserId}
      />
    </div>
  )

  return (
    <SidebarContainer
      ref={containerRef}
      $visible={visible}
      $width={sidebarWidth}
      $isResizing={isResizing}>
      {visible && <ResizeHandle onMouseDown={handleResizeStart} />}
      <ListContainer ref={listRef}>
        {viewMode === 'current-step' ? (
          <>
            {renderThreadsWithNewComment(
              currentStepThreads,
              isNewCommentMode && composingAnchor?.stepId === activeStepId,
              (thread) =>
                renderBubble(thread, () =>
                  scrollToAnchor(getAnchorKey(thread.anchor))
                )
            )}
            {currentStepThreads.length === 0 && !isNewCommentMode && (
              <EmptyText>No comments on this step</EmptyText>
            )}
          </>
        ) : (
          <>
            {workflow.steps.map((step) => {
              const group = sortedGroups.get(step.id)
              const isComposingHere =
                isNewCommentMode && composingAnchor?.stepId === step.id
              const filtered = group?.filter(filterThread) ?? []
              if (filtered.length === 0 && !isComposingHere) return null
              return (
                <StepGroup key={step.id}>
                  <StepGroupHeader>{step.name}</StepGroupHeader>
                  {renderThreadsWithNewComment(
                    filtered,
                    isComposingHere,
                    (thread) =>
                      renderBubble(thread, () =>
                        navigateToStep(thread.anchor.stepId)
                      )
                  )}
                </StepGroup>
              )
            })}
            {filteredOrphaned.length > 0 && (
              <StepGroup>
                <StepGroupHeader $warning>Detached Comments</StepGroupHeader>
                {filteredOrphaned.map((thread) => renderBubble(thread))}
              </StepGroup>
            )}
            {!threads.some(filterThread) && !isNewCommentMode && (
              <EmptyText>No comments to display</EmptyText>
            )}
          </>
        )}
      </ListContainer>
    </SidebarContainer>
  )
}

const SIDEBAR_DEFAULT_WIDTH = 320
const SIDEBAR_MIN_WIDTH = 240
const SIDEBAR_MAX_WIDTH = 600

const SidebarContainer = styled.div<{
  $visible: boolean
  $width: number
  $isResizing: boolean
}>`
  width: ${({ $visible, $width }) => ($visible ? $width : 0)}px;
  min-width: ${({ $visible, $width }) => ($visible ? $width : 0)}px;
  flex-shrink: 0;
  border-left: ${({ $visible, theme }) =>
    $visible ? `1px solid ${theme.forecast.border}` : 'none'};
  background: ${({ theme }) => theme.forecast.background1};
  overflow: hidden;
  position: relative;
  transition: ${({ $isResizing }) =>
    $isResizing
      ? 'none'
      : `width 0.3s ease, min-width 0.3s ease, border-left 0.3s ease`};

  @media print {
    display: none;
  }
`

const ResizeHandle = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  z-index: 2;
  background: transparent;
  transition: background-color 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 2px;
    height: 30px;
    background-color: ${({ theme }) => theme.forecast.secondaryText};
    border-radius: 1px;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover {
    background: ${({ theme }) => theme.forecast.primary}20;

    &::after {
      opacity: 1;
    }
  }

  &:active {
    background: ${({ theme }) => theme.forecast.primary}30;

    &::after {
      opacity: 1;
      background-color: ${({ theme }) => theme.forecast.primary};
    }
  }
`

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  height: 100%;
  overflow-y: auto;
`

const StepGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const StepGroupHeader = styled.div<{ $warning?: boolean }>`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: ${({ $warning, theme }) =>
    $warning ? theme.forecast.warning : theme.forecast.secondaryText};
`

const EmptyText = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 13px;
  padding: 24px 0;
`
